import { APP_CONFIG } from '../constants';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ConfirmationDialog from '../components/ui/ConfirmationDialog';
import { agentInstanceService, agentService } from "../services/agentService";
import {
  billingService,
  type BillingPlanDto,
  type EntitlementDto,
  type MyBillingAccessResponse,
} from '../services/billingService';
import { departmentService } from '../services/departmentService';
import { toast } from 'react-toastify';
import { Bot, Cpu, Eye, Heart, ShieldCheck, Sparkles, TrendingUp, UserPlus, X } from 'lucide-react';
import { Page, PageHeader, Section } from '../components/layout/primitives';
import AiPodMarketplaceGrid from '../components/ai-pod/AiPodMarketplaceGrid';
import { Tabs } from '../components/ui/Tabs';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Checkbox, TextArea, TextField } from '../components/ui/Field';
import { Overlay } from '../components/ui/Overlay';
import { Surface } from '../components/ui/Surface';

type MarketplaceTab = 'AGENTS' | 'PODS';

type CustomAgentType = {
  type: string; 
  description?: string;
  code: string;
  name: string;
  active: boolean;
  capabilities: string[];
  policies: string[];
  metadata?: any;
};

type AgentServer = {
  id: string;
  code: string;
  name: string;
  description?: string | null;
  industry?: { id: string; name: string } | null;
  isActive: boolean;
  metadata?: any;
  schemaJson?: unknown;
  capabilities: any[];
  policies: any[];
  createdAt?: string;
  updatedAt?: string;
  deletedAt?: string | null;
};

type PaginatedAgents = {
  agentTypes: AgentServer[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
};


const toCodeSuggestion = (name: string) =>
  name.trim().toUpperCase().replace(/[^A-Z0-9]+/g, '_').replace(/^_|_$/g, '').slice(0, 40);

const toCapabilityLabel = (raw: string) =>
  raw
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toUpperCase();

const toStableViews = (id: string) => {
  const seed = id.split('').reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
  return 1200 + (seed % 2400);
};

const toStableSuccessRate = (id: string) => {
  const seed = id.split('').reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
  return 90 + (seed % 10);
};

const toStableLatency = (id: string) => {
  const seed = id.split('').reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
  return 120 + (seed % 180);
};

const generateCodeFromName = (name: string) => {
  return name.trim().toUpperCase().replace(/[^A-Z0-9]+/g, '_').replace(/^_|_$/g, '');
};


// Server DTO shapes (for clarity; adapt if your backend differs)
type ServerCapability = { capabilityCode: string; config: Record<string, any> };
type ServerPolicy = { policyCode: string; rules: Record<string, any> };

type CreateAgentDto = {
  code?: string;
  name: string;
  description?: string;
  isActive: boolean;
  metadata?: Record<string, any>;
  capabilities: ServerCapability[];
  policies: ServerPolicy[];
};

type UpdateAgentDto = Partial<CreateAgentDto>;

// Safely parse JSON after a ":" suffix (e.g. sentiment:{"threshold":0.7})
const parseSuffixJSON = (raw: string) => {
  const idx = raw.indexOf(":");
  if (idx === -1) return { key: raw.trim(), obj: {} };
  const key = raw.slice(0, idx).trim();
  const maybeJson = raw.slice(idx + 1).trim();
  try {
    const obj = maybeJson ? JSON.parse(maybeJson) : {};
    return { key, obj };
  } catch {
    console.warn("Invalid JSON after ':' in", raw, "— sending empty config/rules");
    return { key, obj: {} };
  }
};

// Map UI fields → server payload
const toServerPayload = ({
  code,
  name,
  desc,
  active,
  capabilities,
  policies,
  metadataObj,
}: {
  code?: string;
  name: string;
  desc?: string;
  active: boolean;
  capabilities: string[];
  policies: string[];
  metadataObj?: any;
}): CreateAgentDto => {
  const caps: ServerCapability[] = capabilities.map((c) => {
    const { key, obj } = parseSuffixJSON(c);
    return { capabilityCode: key, config: obj };
  });
  const pols: ServerPolicy[] = policies.map((p) => {
    const { key, obj } = parseSuffixJSON(p);
    return { policyCode: key, rules: obj };
  });

  return {
    ...(code ? { code } : {}),
    name,
    description: desc || undefined,
    isActive: active,
    metadata: metadataObj || undefined,
    capabilities: caps,
    policies: pols,
  };
};


function formatPlanPrice(amountMinor: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(amountMinor / 100);
  } catch {
    return `${(amountMinor / 100).toFixed(2)} ${currency}`;
  }
}

/** Checkout requires a synced DB row; metadata-only ids use this prefix. */
const METADATA_PLAN_ID_PREFIX = 'metadata:';

function getMetadataObject(meta: unknown): Record<string, unknown> | null {
  if (meta == null) return null;
  if (typeof meta === 'string') {
    try {
      const o = JSON.parse(meta) as unknown;
      return typeof o === 'object' && o !== null ? (o as Record<string, unknown>) : null;
    } catch {
      return null;
    }
  }
  if (typeof meta === 'object') return meta as Record<string, unknown>;
  return null;
}

/** Matches core-ms: `metadata.pricing` first, else `schemaJson.pricing`; free unless `isFree === false`. */
function pricingTierFromBlock(pricing: unknown): 'FREE' | 'PREMIUM' {
  if (!pricing || typeof pricing !== 'object') return 'FREE';
  const p = pricing as Record<string, unknown>;
  return p.isFree === false ? 'PREMIUM' : 'FREE';
}

function getAgentPricingTier(agt: { metadata?: unknown; schemaJson?: unknown }): 'FREE' | 'PREMIUM' {
  const metaRoot = getMetadataObject(agt.metadata);
  if (metaRoot && metaRoot.pricing !== undefined && metaRoot.pricing !== null) {
    return pricingTierFromBlock(metaRoot.pricing);
  }
  const schemaRoot = getMetadataObject(agt.schemaJson);
  if (schemaRoot && schemaRoot.pricing !== undefined && schemaRoot.pricing !== null) {
    return pricingTierFromBlock(schemaRoot.pricing);
  }
  return 'FREE';
}

/** When billing API returns no rows yet, show plans from `metadata.pricing` (string or object). */
function billingPlansFromAgentMetadata(meta: unknown): BillingPlanDto[] {
  const root = getMetadataObject(meta);
  if (!root) return [];
  const pricing = root.pricing as Record<string, unknown> | undefined;
  if (!pricing || typeof pricing !== 'object') return [];
  if (pricing.isFree === true) return [];
  const rawPlans = pricing.plans;
  if (!Array.isArray(rawPlans) || rawPlans.length === 0) return [];

  return rawPlans.map((item, index) => {
    const p = item as Record<string, unknown>;
    const planKey =
      typeof p.planKey === 'string' && p.planKey.trim() ? p.planKey.trim() : `plan-${index}`;
    const amountMinor =
      typeof p.amountMinor === 'number' && Number.isFinite(p.amountMinor) ? p.amountMinor : 0;
    let currency = typeof p.currency === 'string' ? p.currency.trim().toUpperCase() : 'USD';
    if (currency.length !== 3) currency = 'USD';

    return {
      id: `${METADATA_PLAN_ID_PREFIX}${planKey}`,
      planKey,
      billingMode: 'SUBSCRIPTION' as const,
      displayName: typeof p.displayName === 'string' ? p.displayName : planKey,
      description: typeof p.description === 'string' ? p.description : null,
      amountMinor,
      currency,
      interval: typeof p.interval === 'string' ? p.interval : 'month',
      intervalCount:
        typeof p.intervalCount === 'number' && p.intervalCount > 0 ? p.intervalCount : 1,
      stripePriceId: null,
      lastSyncedAt: null,
    };
  });
}

function formatPlanBillingCadence(plan: BillingPlanDto): string {
  if (!plan.interval) {
    return 'Subscription';
  }
  const n = plan.intervalCount ?? 1;
  const interval = plan.interval;
  const unit =
    interval === 'month'
      ? 'month'
      : interval === 'year'
        ? 'year'
        : interval === 'week'
          ? 'week'
          : interval === 'day'
            ? 'day'
            : interval;
  if (n === 1) {
    return `Per ${unit}`;
  }
  return `Every ${n} ${unit}s`;
}

type AgentBillingTag = 'PENDING' | 'SUBSCRIBED' | 'PAID';

function getAgentBillingTag(
  agentTypeId: string,
  access: MyBillingAccessResponse | null
): AgentBillingTag | null {
  if (!access) return null;
  const ent = access.entitlements.find((e) => e.agentTypeId === agentTypeId);
  if (ent) {
    return ent.sourceType === 'SUBSCRIPTION' ? 'SUBSCRIBED' : 'PAID';
  }
  const pending = (access.pendingPayments ?? []).find((p) => p.billingPlan?.agentTypeId === agentTypeId);
  return pending ? 'PENDING' : null;
}

function getEntitlementForAgent(
  agentTypeId: string,
  access: MyBillingAccessResponse | null
): EntitlementDto | undefined {
  return access?.entitlements.find((e) => e.agentTypeId === agentTypeId);
}

function planMatchesEntitlement(plan: BillingPlanDto, ent: EntitlementDto | undefined): boolean {
  if (!ent) return false;
  const sid = ent.subscription?.billingPlan?.id;
  const pid = ent.payment?.billingPlan?.id;
  const skey = ent.subscription?.billingPlan?.planKey;
  const pkey = ent.payment?.billingPlan?.planKey;
  if (sid && plan.id === sid) return true;
  if (pid && plan.id === pid) return true;
  if (skey && plan.planKey === skey) return true;
  if (pkey && plan.planKey === pkey) return true;
  return false;
}

function AgentAccessTag({ tag }: { tag: AgentBillingTag }) {
  const styles: Record<AgentBillingTag, string> = {
    PENDING: 'bg-amber-100 text-amber-900 border-amber-200',
    SUBSCRIBED: 'bg-sky-100 text-sky-900 border-sky-200',
    PAID: 'bg-emerald-100 text-emerald-900 border-emerald-200',
  };
  const labels: Record<AgentBillingTag, string> = {
    PENDING: 'Pending',
    SUBSCRIBED: 'Subscribed',
    PAID: 'Paid',
  };
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${styles[tag]}`}
    >
      {labels[tag]}
    </span>
  );
}

function PricingTierTag({ tier }: { tier: 'FREE' | 'PREMIUM' }) {
  if (tier === 'PREMIUM') {
    return (
      <Badge className="inline-flex items-center rounded-full border border-violet-200 bg-violet-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-violet-900">
        Premium
      </Badge>
    );
  }
  return (
    <Badge className="inline-flex items-center rounded-full border border-slate-200 bg-slate-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-slate-700">
      Free
    </Badge>
  );
}

export const AgentTypes: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeMarketplaceTab, setActiveMarketplaceTab] = useState<MarketplaceTab>(
    searchParams.get('tab') === 'pods' ? 'PODS' : 'AGENTS'
  );
  const [activeIndustryTab, setActiveIndustryTab] = useState<string>('ALL');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newTypeCode, setNewTypeCode] = useState('');
  const [newTypeName, setNewTypeName] = useState('');
  const [newTypeDescription, setNewTypeDescription] = useState('');
  const [newTypeActive, setNewTypeActive] = useState(true);
  const [newTypeCapabilities, setNewTypeCapabilities] = useState<string[]>([]);
  const [newTypePolicies, setNewTypePolicies] = useState<string[]>([]);
  const [newTypeMetadata, setNewTypeMetadata] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [customTypes, setCustomTypes] = useState<CustomAgentType[]>([]);



  // editing state
  const [isEditing, setIsEditing] = useState(false);
  const [editOriginalTypeName, setEditOriginalTypeName] = useState<string | null>(null);


// ...inside your component:
const [confirmOpen, setConfirmOpen] = useState(false);
const [deleting, setDeleting] = useState(false);

const [saving, setSaving] = useState(false);

// server-backed listing
const [serverTypes, setServerTypes] = useState<AgentServer[]>([]);
const [loading, setLoading] = useState(false);
const [page, setPage] = useState(1);
const [limit, setLimit] = useState(10);
const [total, setTotal] = useState(0);
const [totalPages, setTotalPages] = useState(1);

// server filters (align to your endpoint)
const [onlyActive, setOnlyActive] = useState<boolean | undefined>(true);
const [includeDeleted, setIncludeDeleted] = useState(false);
const [searchQuery, setSearchQuery] = useState('');
const [isDetailsDrawerOpen, setIsDetailsDrawerOpen] = useState(false);
const [isDetailsDrawerMounted, setIsDetailsDrawerMounted] = useState(false);
const [selectedAgentType, setSelectedAgentType] = useState<AgentServer | null>(null);
const [detailsTab, setDetailsTab] = useState<'overview' | 'skills' | 'performance' | 'technical'>('overview');
const [isHireConfirmOpen, setIsHireConfirmOpen] = useState(false);
const drawerCloseTimeoutRef = useRef<number | null>(null);
const [hiring, setHiring] = useState(false);
const [billingPlans, setBillingPlans] = useState<BillingPlanDto[]>([]);
const [billingPlansLoading, setBillingPlansLoading] = useState(false);
const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);

const metadataBillingPlans = useMemo(
  () => (selectedAgentType ? billingPlansFromAgentMetadata(selectedAgentType.metadata) : []),
  [selectedAgentType]
);

const hireModalPlans = useMemo(() => {
  if (billingPlans.length > 0) return billingPlans;
  return metadataBillingPlans;
}, [billingPlans, metadataBillingPlans]);

const [myBillingAccess, setMyBillingAccess] = useState<MyBillingAccessResponse | null>(null);

const refreshMyBillingAccess = React.useCallback(async () => {
  try {
    const data = await billingService.getMyBillingAccess();
    setMyBillingAccess(data);
  } catch {
    setMyBillingAccess(null);
  }
}, []);

const entitlementForSelected = useMemo(() => {
  if (!selectedAgentType?.id || !myBillingAccess) return undefined;
  return getEntitlementForAgent(selectedAgentType.id, myBillingAccess);
}, [selectedAgentType?.id, myBillingAccess]);

const refreshList = async () => {
  setLoading(true);
  try {
    const data =
      await agentService.listAgentTypes({
        
        page,
        limit,
        search: searchQuery || "",
        isActive: typeof onlyActive === "boolean" ? onlyActive : undefined,
        includeDeleted,
      });

    // data may be { data: {...} } or direct; normalize:
    const payload: PaginatedAgents = (data as any)?.agentTypes
      ? (data as any)
      : (data as any)?.data ?? (data as any);

    setServerTypes(payload.agentTypes || []);
    setTotal(payload.total || 0);
    setTotalPages(payload.totalPages || 1);
  } catch (e) {
    console.error("Failed to load agent types:", e);
  } finally {
    setLoading(false);
  }
};

useEffect(() => {
  let t = setTimeout(() => {
    refreshList();
  }, 300);
  return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [page, limit, onlyActive, includeDeleted, searchQuery]);

  useEffect(() => {
    const b = searchParams.get('billing');
    if (b === 'success') {
      toast.success('Payment successful. You can open Agents to hire and configure your agent.');
      void refreshMyBillingAccess();
      const next = new URLSearchParams(searchParams);
      next.delete('billing');
      next.delete('agentTypeId');
      setSearchParams(next, { replace: true });
    } else if (b === 'cancel') {
      toast.info('Checkout was canceled.');
      void refreshMyBillingAccess();
      const next = new URLSearchParams(searchParams);
      next.delete('billing');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams, refreshMyBillingAccess]);

  useEffect(() => {
    void refreshMyBillingAccess();
  }, [refreshMyBillingAccess]);

  useEffect(() => {
    if (!selectedAgentType?.id) {
      setBillingPlans([]);
      setSelectedPlanId(null);
      return;
    }
    let cancelled = false;
    setBillingPlans([]);
    (async () => {
      setBillingPlansLoading(true);
      try {
        const res = await billingService.listBillingPlans(selectedAgentType.id);
        if (cancelled) return;
        const plans = res.plans || [];
        setBillingPlans(plans);
      } catch {
        if (!cancelled) {
          setBillingPlans([]);
        }
      } finally {
        if (!cancelled) setBillingPlansLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedAgentType?.id]);

  useEffect(() => {
    setSelectedPlanId((prev) => {
      const ent = entitlementForSelected;
      const selectable = (p: BillingPlanDto) => !planMatchesEntitlement(p, ent);
      if (hireModalPlans.length === 0) return null;
      if (hireModalPlans.some((p) => p.id === prev)) {
        const cur = hireModalPlans.find((p) => p.id === prev);
        if (cur && !selectable(cur)) {
          const alt = hireModalPlans.find(selectable);
          return alt?.id ?? prev;
        }
        return prev;
      }
      const firstSelectable = hireModalPlans.find(selectable);
      return firstSelectable?.id ?? hireModalPlans[0]?.id ?? null;
    });
  }, [hireModalPlans, entitlementForSelected]);


const serverByName = useMemo(() => {
  const m = new Map<string, AgentServer>();
  for (const s of serverTypes) m.set((s.name || "").trim(), s);
  return m;
}, [serverTypes]);

const [deleteId, setDeleteId] = useState<string | null>(null);
const [editId, setEditId] = useState<string | null>(null);


const askDelete = (id: string) => {
  setDeleteId(id);
  setConfirmOpen(true);
};

const doDelete = async () => {
  if (!deleteId) return;
  try {
    setDeleting(true);
    await agentService.deleteAgentType(deleteId);
    toast.success("Deleted successfully")

    await refreshList();
  } catch (e) {
    console.error("❌ Server delete failed:", e);
  } finally {
    setDeleting(false);
    setConfirmOpen(false);
    setDeleteId(null);
  }
};




  // Load custom types from localStorage on mount
  useEffect(() => {
    try {
      const raw = localStorage.getItem('customAgentTypes');
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          const normalized: CustomAgentType[] = parsed
            .filter((t: any) => typeof t?.type === 'string')
            .map((t: any) => ({
              type: t.type,
              description: t.description,
              code: t.code || '',
              name: t.name || t.type,
              active: typeof t.active === 'boolean' ? t.active : true,
              capabilities: Array.isArray(t.capabilities) ? t.capabilities : [],
              policies: Array.isArray(t.policies) ? t.policies : [],
              metadata: t.metadata,
            }));
          setCustomTypes(normalized);
        }
      }
    } catch {
   
    }
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem('customAgentTypes', JSON.stringify(customTypes));
    } catch {
      
    }
  }, [customTypes]);
  const industryTabs = useMemo(() => {
    const unique = Array.from(
      new Set(
        serverTypes
          .map((item) => item.industry?.name?.trim())
          .filter((name): name is string => !!name)
      )
    ).sort((a, b) => a.localeCompare(b));

    return ['ALL', ...unique];
  }, [serverTypes]);
  const hasMultipleIndustries = industryTabs.filter((t) => t !== 'ALL').length > 1;

  const filteredServerTypes = useMemo(() => {
    if (activeIndustryTab === 'ALL') return serverTypes;
    return serverTypes.filter(
      (item) => (item.industry?.name?.trim() || '') === activeIndustryTab
    );
  }, [serverTypes, activeIndustryTab]);

  useEffect(() => {
    if (!industryTabs.includes(activeIndustryTab)) {
      setActiveIndustryTab('ALL');
    }
  }, [industryTabs, activeIndustryTab]);
  
  const resetForm = () => {
    setNewTypeCode('');
    setNewTypeName('');
    setNewTypeDescription('');
    setNewTypeActive(true);
    setNewTypeCapabilities([]);
    setNewTypePolicies([]);
    setNewTypeMetadata('');
    setError(null);
    setIsEditing(false);
    setEditOriginalTypeName(null);
  };

  const openCreateModal = () => {
    resetForm();
    setIsModalOpen(true);
  };

  const openEditModal = (agt: AgentServer) => {
    setEditId(agt?.id);
    setError(null);
    setIsEditing(true);
  
    setEditOriginalTypeName(agt.name || agt.code);
  
    setNewTypeCode(agt.code || '');
    setNewTypeName(agt.name || '');
    setNewTypeDescription(agt.description || '');
    setNewTypeActive(!!agt.isActive);
  
 
    const caps = (agt.capabilities || []).map((c: any) => {
      if (typeof c === 'string') return c;
      const code = c.capabilityCode || c.code || c.name || '';
      const cfg = c.config && Object.keys(c.config).length ? `:${JSON.stringify(c.config)}` : '';
      return `${code}${cfg}`;
    });
    const pols = (agt.policies || []).map((p: any) => {
      if (typeof p === 'string') return p;
      const code = p.policyCode || p.code || p.name || '';
      const rules = p.rules && Object.keys(p.rules).length ? `:${JSON.stringify(p.rules)}` : '';
      return `${code}${rules}`;
    });
  
    setNewTypeCapabilities(caps);
    setNewTypePolicies(pols);
  
    setNewTypeMetadata(agt.metadata ? JSON.stringify(agt.metadata, null, 2) : '');
  
    setIsModalOpen(true);
  };
  
  

  const handleCreateOrUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
  
    const code = newTypeCode.trim().toUpperCase();
    const name = newTypeName.trim();
    const desc = newTypeDescription.trim();
    const localCode = code || toCodeSuggestion(name) || "AGENT";
  
    if (!name) {
      setError("Type name is required.");
      return;
    }
  
    let metadataObj: any = undefined;
    if (newTypeMetadata.trim()) {
      try {
        metadataObj = JSON.parse(newTypeMetadata);
      } catch {
        setError("Invalid JSON in metadata.");
        return;
      }
    }
  
    const cleanedCapabilities = Array.from(
      new Set(newTypeCapabilities.map((c) => c.trim()).filter(Boolean))
    );
    const cleanedPolicies = Array.from(
      new Set(newTypePolicies.map((p) => p.trim()).filter(Boolean))
    );
  
    // ------- Build payloads -------
    const consolePayload = {
      code,
      name,
      type: name,
      description: desc || undefined,
      active: newTypeActive,
      capabilities: cleanedCapabilities,
      policies: cleanedPolicies,
      metadata: metadataObj,
    };
  
    const serverPayload = toServerPayload({
      code,
      name,
      desc,
      active: newTypeActive,
      capabilities: cleanedCapabilities,
      policies: cleanedPolicies,
      metadataObj,
    });
  

    // ------- Optimistic local update (keep cards instant) -------
    const newItem: CustomAgentType = {
      type: name,
      name,
      code: localCode,
      description: desc || undefined,
      active: newTypeActive,
      capabilities: cleanedCapabilities,
      policies: cleanedPolicies,
      metadata: metadataObj,
    };
  
    if (isEditing && editOriginalTypeName) {
      setCustomTypes((prev) => {
        const idx = prev.findIndex(
          (ct) => ct.type.toLowerCase() === editOriginalTypeName.toLowerCase()
        );
        if (idx === -1) return [...prev, newItem];
        const copy = [...prev];
        copy[idx] = newItem;
        return copy;
      });
    } else {
      setCustomTypes((prev) => [...prev, newItem]);
    }
  
    setSaving(true);
    try {
      if (isEditing && editOriginalTypeName) {
        const res = await agentService.updateAgentType(String(editId), serverPayload);
        toast.success("Updated successfully")
        await refreshList()
      } else {
        const res = await agentService.createAgentType(serverPayload);
        toast.success("Created successfully")
        await refreshList()

        if (res && (res as any).id) {
          setCustomTypes((prev) =>
            prev.map((ct) =>
              ct.code === localCode
                ? {
                    ...ct,
                    metadata: {
                      ...(ct.metadata || {}),
                      id: (res as any).id,
                    },
                  }
                : ct
            )
          );
        } else if (res && (res as any)._id) {
          setCustomTypes((prev) =>
            prev.map((ct) =>
              ct.code === localCode
                ? {
                    ...ct,
                    metadata: {
                      ...(ct.metadata || {}),
                      _id: (res as any)._id,
                    },
                  }
                : ct
            )
          );
        }
      }
    } catch (err) {
      console.error("❌ Save failed:", err);

    } finally {
      setSaving(false);
    }

    setIsModalOpen(false);
    resetForm();
  };
  
  

  const openDetailsDrawer = (agentType: AgentServer) => {
    if (drawerCloseTimeoutRef.current !== null) {
      window.clearTimeout(drawerCloseTimeoutRef.current);
      drawerCloseTimeoutRef.current = null;
    }
    setSelectedAgentType(agentType);
    setDetailsTab('overview');
    setIsDetailsDrawerMounted(true);
    requestAnimationFrame(() => setIsDetailsDrawerOpen(true));
  };

  const closeDetailsDrawer = () => {
    setIsDetailsDrawerOpen(false);
    setIsHireConfirmOpen(false);
    drawerCloseTimeoutRef.current = window.setTimeout(() => {
      setIsDetailsDrawerMounted(false);
      setSelectedAgentType(null);
      drawerCloseTimeoutRef.current = null;
    }, 320);
  };

  useEffect(() => {
    return () => {
      if (drawerCloseTimeoutRef.current !== null) {
        window.clearTimeout(drawerCloseTimeoutRef.current);
      }
    };
  }, []);

  const confirmHireAgent = async () => {
    if (!selectedAgentType) return;

    if (hireModalPlans.length > 0) {
      if (!selectedPlanId) {
        toast.error('Select a plan to continue.');
        return;
      }
      const selectedPlanRow = hireModalPlans.find((p) => p.id === selectedPlanId);
      if (selectedPlanRow && planMatchesEntitlement(selectedPlanRow, entitlementForSelected)) {
        toast.info('You already have this plan.');
        return;
      }
      if (selectedPlanId.startsWith(METADATA_PLAN_ID_PREFIX)) {
        toast.error(
          'This plan is not synced to billing yet (no Stripe price). Re-save the agent type in admin with Stripe configured, wait for sync, then try again.'
        );
        return;
      }
      setHiring(true);
      try {
        const origin = window.location.origin;
        const agentTypeId = selectedAgentType.id;
        const successUrl = `${origin}/agent-types?billing=success&agentTypeId=${encodeURIComponent(agentTypeId)}`;
        const cancelUrl = `${origin}/agent-types?billing=cancel`;
        const res = await billingService.createCheckoutSession({
          billingPlanId: selectedPlanId,
          successUrl,
          cancelUrl,
        });
        if (res.checkoutUrl) {
          window.location.href = res.checkoutUrl;
          return;
        }
        toast.error('No checkout URL returned.');
      } catch (err: unknown) {
        console.error('Checkout failed', err);
        const msg = err instanceof Error ? err.message : 'Failed to start checkout';
        toast.error(msg);
      } finally {
        setHiring(false);
      }
      return;
    }

    setHiring(true);
    try {
      const name = selectedAgentType.name || selectedAgentType.code || 'Agent';
      const code = `${generateCodeFromName(name)}_${Date.now().toString().slice(-6)}`;
      const departmentsRes: any = await departmentService.list({ page: 1, limit: 100 });
      const departments = departmentsRes?.departments || departmentsRes?.data?.departments || [];
      const fallbackDepartmentId = Array.isArray(departments) && departments.length ? String(departments[0].id) : '';
      const metadataDepartmentId = String(selectedAgentType?.metadata?.departmentId || '').trim();
      const departmentId = metadataDepartmentId || fallbackDepartmentId;

      if (!departmentId) {
        toast.error('No department found. Please create a department first.');
        return;
      }

      const payload: Record<string, any> = {
        name,
        description: selectedAgentType.description || '',
        shortDescription: selectedAgentType.description || '',
        isActive: true,
        code,
        departmentId,
        industryId: selectedAgentType.industry?.id || undefined,
        categoryId: undefined,
        agentTypeId: selectedAgentType.id,
        useImageInChat: false,
      };

      const res: any = await agentInstanceService.createAgentInstance(payload);
      toast.success(res?.message || `${name} hired successfully!`);
      closeDetailsDrawer();
      navigate('/agents');
    } catch (err: any) {
      console.error('Failed to hire agent', err);
      toast.error(err?.message || 'Failed to hire agent');
    } finally {
      setHiring(false);
    }
  };

  const selectedCapabilities = (selectedAgentType?.capabilities || [])
    .map((cap: any) => (typeof cap === 'string' ? cap : cap?.capabilityCode || cap?.code || cap?.name || ''))
    .filter(Boolean)
    .slice(0, 6);

  const selectedPolicies = (selectedAgentType?.policies || [])
    .map((pol: any) => (typeof pol === 'string' ? pol : pol?.policyCode || pol?.code || pol?.name || ''))
    .filter(Boolean)
    .slice(0, 6);
  const totalMarketplaceAgents = filteredServerTypes.length;

  const drawerBillingTag = selectedAgentType
    ? getAgentBillingTag(selectedAgentType.id, myBillingAccess)
    : null;
  const drawerPricingTier = selectedAgentType ? getAgentPricingTier(selectedAgentType) : 'FREE';
  const drawerHireCta =
    drawerBillingTag === 'PENDING'
      ? 'Complete payment'
      : drawerBillingTag === 'SUBSCRIBED' || drawerBillingTag === 'PAID'
        ? 'Manage plan'
        : 'Hire Now';

  const hireModalSelectedPlan = hireModalPlans.find((p) => p.id === selectedPlanId);
  const hireModalSelectedIsOwned = hireModalSelectedPlan
    ? planMatchesEntitlement(hireModalSelectedPlan, entitlementForSelected)
    : false;
  const hireModalCanUpdatePlan =
    !!entitlementForSelected &&
    hireModalPlans.length > 1 &&
    !hireModalSelectedIsOwned &&
    !!selectedPlanId &&
    !selectedPlanId.startsWith(METADATA_PLAN_ID_PREFIX);

  let hireModalPrimaryLabel = 'Confirm & Hire';
  if (hireModalPlans.length > 0) {
    if (selectedPlanId?.startsWith(METADATA_PLAN_ID_PREFIX)) {
      hireModalPrimaryLabel = 'Awaiting billing sync';
    } else if (hireModalSelectedIsOwned) {
      hireModalPrimaryLabel = 'Current plan';
    } else if (hireModalCanUpdatePlan) {
      hireModalPrimaryLabel = 'Update plan';
    } else {
      hireModalPrimaryLabel = 'Continue to payment';
    }
  }

  const hireModalPrimaryDisabled =
    hiring ||
    (hireModalPlans.length > 0 &&
      (!selectedPlanId ||
        selectedPlanId.startsWith(METADATA_PLAN_ID_PREFIX) ||
        hireModalSelectedIsOwned));

  return (
    <Page backgroundClassName="bg-[linear-gradient(180deg,#FFFFFF_0%,#4CC4D012_100%)]" contentClassName="pb-10 pt-6">
      <PageHeader
        title={
          <>
            Explore your{' '}
            <span className="bg-gradient-to-r from-c-805dc6 to-c-3683c6 bg-clip-text text-transparent">
              Agent Marketplace
            </span>
          </>
        }
        subtitle="Discover role-ready agent types across industries, compare capabilities, and hire the right assistant for your workflows."
        aside={
          <div className="flex items-end gap-3 lg:pb-2">
            <p className="text-[4.4rem] font-semibold leading-none text-transparent [-webkit-text-stroke:2px_#b7bdf1] sm:text-[5.3rem] lg:text-[6.1rem]">
              {totalMarketplaceAgents}
            </p>
            <p className="pb-2 text-xl font-medium text-c-53586b [writing-mode:vertical-rl] [transform:rotate(180deg)]">AI agents</p>
          </div>
        }
      />

      <Section className="pb-2 pt-0">
        <Tabs
          variant="config"
          items={[
            { id: 'AGENTS', label: 'Agent Types' },
            { id: 'PODS', label: APP_CONFIG.PODS_TAB_LABEL },
          ]}
          value={activeMarketplaceTab}
          onChange={(tab) => {
            const next = tab as MarketplaceTab;
            setActiveMarketplaceTab(next);
            setSearchParams((prev) => {
              const params = new URLSearchParams(prev);
              if (next === 'PODS') params.set('tab', 'pods');
              else params.delete('tab');
              return params;
            });
          }}
        />
      </Section>

      {activeMarketplaceTab === 'AGENTS' && (
        <Section className="border-b border-c-e3e7f3 pb-6 pt-0">
          <div className="grid gap-4 lg:grid-cols-[1fr_auto] lg:items-center">
            <div className="relative min-w-0 w-full max-w-[640px]">
              <TextField
                type="text"
                placeholder="Search by name or purpose..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full rounded-xl border border-c-c3c3c3 bg-white px-4 py-3 pr-11 text-[1rem] text-c-1f1f1f placeholder:text-c-9095a9 focus:border-c-8f8f8f focus:outline-none"
              />
              <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-c-5f79ce">⌕</span>
            </div>
          </div>
          {hasMultipleIndustries && (
            <Tabs
              variant="config"
              className="mt-4 pr-0"
              items={industryTabs.map((tab) => ({
                id: tab,
                label: tab === 'ALL' ? 'All Industries' : tab,
              }))}
              value={activeIndustryTab}
              onChange={setActiveIndustryTab}
            />
          )}
        </Section>
      )}

      {activeMarketplaceTab === 'PODS' && (
        <Section className="py-8">
          <AiPodMarketplaceGrid />
        </Section>
      )}

      {activeMarketplaceTab === 'AGENTS' && (
      <Section className="py-8">
        {loading ? (
          <div className="py-16 text-center">Loading…</div>
        ) : filteredServerTypes.length === 0 ? (
          <div className="py-16 text-center">
            <div className="mb-4 text-6xl">🏷️</div>
            <h3 className="mb-2 text-xl font-semibold text-agentic-text-primary">
              No agent types found
            </h3>
            <p className="text-agentic-text-secondary">
              {activeIndustryTab === 'ALL'
                ? 'Create an agent type to get started.'
                : `No agent types found for ${activeIndustryTab}.`}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 items-stretch gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {filteredServerTypes.map((agt) => {
              const agtBillingTag = getAgentBillingTag(agt.id, myBillingAccess);
              return (
              <Surface
                key={agt.id}
                className="flex min-h-[430px] flex-col rounded-2xl border border-agentic-gray-200/80 bg-white p-3.5 shadow-sm transition-shadow duration-300 ease-agentic hover:shadow-md"
              >
                <div className="flex items-center justify-between">
                  <div className="inline-flex items-center gap-1.5 text-xs font-medium text-agentic-text-secondary">
                    <Bot className="h-3.5 w-3.5 text-agentic-primary-blue" />
                    <span>AI Assistant</span>
                  </div>
                  <Button tone="custom"
                    type="button"
                    className="text-agentic-gray-400 transition-colors hover:text-agentic-primary-blue"
                    aria-label="Save agent type"
                  >
                    <Heart className="h-4 w-4" />
                  </Button>
                </div>

                <div className="mt-3 flex justify-center">
                  <div className="flex h-16 w-16 items-center justify-center rounded-full border-2 border-agentic-gray-100 bg-gradient-to-br from-agentic-primary-blue/20 to-agentic-primary-blue-light/20">
                    <span className="text-base font-bold text-agentic-primary-blue">
                      {(agt.name || agt.code || 'A')
                        .split(' ')
                        .slice(0, 2)
                        .map((x) => x[0] || '')
                        .join('')
                        .toUpperCase()}
                    </span>
                  </div>
                </div>

                <div className="mt-3 min-w-0 text-center">
                  <div className="mb-1.5 flex min-h-[22px] flex-wrap items-center justify-center gap-1.5">
                    <PricingTierTag tier={getAgentPricingTier(agt)} />
                    {agtBillingTag ? <AgentAccessTag tag={agtBillingTag} /> : null}
                  </div>
                  <h3 className="min-h-[56px] text-xl font-semibold leading-tight text-agentic-text-primary line-clamp-2">
                    {agt.name || agt.code}
                  </h3>
                  <p className="mt-1 text-xs font-semibold uppercase tracking-wide text-agentic-primary-blue">
                    {agt.industry?.name || 'General'}
                  </p>
                  <p className="mt-1.5 min-h-[48px] text-sm leading-snug text-agentic-text-secondary line-clamp-3">
                    {agt.description || 'No description available for this agent type.'}
                  </p>
                </div>

                <div className="mt-2.5 flex items-center justify-center gap-1.5 text-xs text-agentic-text-muted">
                  <Eye className="h-3.5 w-3.5" />
                  <span>{toStableViews(agt.id)}</span>
                </div>

                <div className="mt-3 flex min-h-[56px] flex-wrap content-start justify-center gap-1.5">
                  {(agt.capabilities || [])
                    .slice(0, 3)
                    .map((cap: any, idx: number) => {
                      const capCode =
                        typeof cap === 'string'
                          ? cap
                          : cap?.capabilityCode || cap?.code || cap?.name || '';

                      if (!capCode) return null;
                      return (
                        <Badge tone="neutral"
                          key={`${agt.id}-cap-${idx}`}
                          className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold"
                        >
                          {toCapabilityLabel(capCode)}
                        </Badge>
                      );
                    })}
                  {(agt.capabilities?.length || 0) > 3 && (
                    <Badge className="inline-flex items-center rounded-full border border-agentic-gray-300 px-2.5 py-0.5 text-[11px] font-semibold text-agentic-text-primary">
                      +{agt.capabilities.length - 3}
                    </Badge>
                  )}
                </div>

                <Button tone="custom"
                  type="button"
                  onClick={() => openDetailsDrawer(agt)}
                  className="mt-auto flex w-full items-center justify-center rounded-[18px] bg-gradient-to-r from-agentic-accent-purple to-agentic-primary-blue-light px-4 py-2 text-sm font-semibold text-white transition-all hover:shadow-md"
                >
                  {agtBillingTag === 'PENDING'
                    ? 'Complete setup'
                    : agtBillingTag === 'SUBSCRIBED' || agtBillingTag === 'PAID'
                      ? 'Manage'
                      : 'Hire Me'}
                </Button>
              </Surface>
            );
            })}
          </div>
        )}
      </Section>
      )}

      {isDetailsDrawerMounted && selectedAgentType && (
        <>
          <div
            className={`fixed inset-0 z-40 bg-black/40 backdrop-blur-[2px] transition-opacity duration-300 ${
              isDetailsDrawerOpen ? 'opacity-100' : 'pointer-events-none opacity-0'
            }`}
            onClick={closeDetailsDrawer}
          />
          <div
            className={`fixed inset-y-0 right-0 z-50 flex w-full max-w-4xl flex-col border-l border-agentic-gray-200 bg-white text-sm shadow-2xl transition-transform duration-300 ease-out ${
              isDetailsDrawerOpen ? 'translate-x-0' : 'translate-x-full'
            }`}
          >
            <div className="px-5 py-3 border-b border-agentic-gray-200 flex items-start justify-between gap-4">
              <div className="flex items-start gap-4">
                <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-agentic-accent-purple to-agentic-primary-blue-light text-white flex items-center justify-center shadow-sm">
                  <Sparkles className="h-5 w-5" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-agentic-text-primary">Agent Details</h2>
                  <p className="text-sm text-agentic-text-secondary">View profile and hire this agent</p>
                  <div className="mt-2 flex flex-wrap items-center gap-1.5">
                    <PricingTierTag tier={drawerPricingTier} />
                    {drawerBillingTag ? (
                      <AgentAccessTag tag={drawerBillingTag} />
                    ) : (
                      <Badge className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-[11px] font-semibold text-green-700">
                        <ShieldCheck className="h-3.5 w-3.5" />
                        Available
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button tone="custom"
                  type="button"
                  onClick={() => setIsHireConfirmOpen(true)}
                  className="px-4 py-2 rounded-xl bg-black text-white text-sm font-semibold hover:opacity-90"
                >
                  {drawerHireCta}
                </Button>
                <Button tone="custom"
                  type="button"
                  onClick={closeDetailsDrawer}
                  className="h-10 w-10 rounded-lg border border-agentic-gray-200 flex items-center justify-center text-agentic-text-secondary hover:bg-agentic-gray-50"
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>
            </div>

            <div className="px-5 py-4 bg-agentic-gray-50 border-b border-agentic-gray-200">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="inline-flex items-center gap-2 rounded-full border border-agentic-gray-200 bg-white px-3 py-1 text-[11px] font-semibold text-agentic-text-secondary">
                  <Bot className="h-3.5 w-3.5 text-agentic-primary-blue" />
                  AI ASSISTANT
                </div>
                <div className="inline-flex items-center rounded-full border border-agentic-gray-200 bg-white px-3 py-1 text-[11px] font-semibold text-agentic-text-secondary">
                  V1.0
                </div>
              </div>
              <div className="mt-4 flex items-start gap-3">
                <div className="h-16 w-16 rounded-full border-2 border-agentic-gray-200 bg-white text-agentic-primary-blue flex items-center justify-center text-xl font-semibold">
                  {(selectedAgentType.name || 'A')
                    .split(' ')
                    .slice(0, 2)
                    .map((x) => x[0] || '')
                    .join('')
                    .toUpperCase()}
                </div>
                <div className="min-w-0">
                  <h3 className="text-xl font-semibold text-agentic-text-primary leading-tight line-clamp-2">{selectedAgentType.name}</h3>
                  <p className="text-agentic-text-secondary mt-1 text-sm line-clamp-1">{selectedAgentType.description || 'No description available'}</p>
                </div>
              </div>
              <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-lg">
                <Surface tone="card-agentic" className="rounded-2xl border p-3.5">
                  <div className="flex items-center gap-2 text-agentic-text-primary text-xl font-semibold">
                    <Eye className="h-5 w-5 text-agentic-primary-blue" />
                    {toStableViews(selectedAgentType.id)}
                  </div>
                  <div className="mt-1.5 text-xs text-agentic-text-secondary">Views</div>
                </Surface>
                <Surface tone="card-agentic" className="rounded-2xl border p-3.5">
                  <div className="flex items-center gap-2 text-agentic-text-primary text-xl font-semibold">
                    <TrendingUp className="h-5 w-5 text-green-600" />
                    {toStableSuccessRate(selectedAgentType.id)}%
                  </div>
                  <div className="mt-1.5 text-xs text-agentic-text-secondary">Success Rate</div>
                </Surface>
              </div>
            </div>

            <div className="px-5 pt-3 border-b border-agentic-gray-200">
              <div className="flex items-center gap-6 overflow-x-auto">
                {[
                  { key: 'overview', label: 'Overview' },
                  { key: 'skills', label: 'Skills & Capabilities' },
                  { key: 'performance', label: 'Performance' },
                  { key: 'technical', label: 'Technical Details' },
                ].map((tab) => (
                  <Button tone="custom"
                    key={tab.key}
                    type="button"
                    onClick={() => setDetailsTab(tab.key as typeof detailsTab)}
                    className={`pb-2.5 text-sm font-medium whitespace-nowrap border-b-2 ${
                      detailsTab === tab.key
                        ? 'border-agentic-primary-blue text-agentic-text-primary'
                        : 'border-transparent text-agentic-text-secondary hover:text-agentic-text-primary'
                    }`}
                  >
                    {tab.label}
                  </Button>
                ))}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              {detailsTab === 'overview' && (
                <>
                  <div className="rounded-2xl border border-agentic-gray-200 p-5">
                    <h4 className="text-xl font-semibold text-agentic-text-primary mb-2.5">About This Agent</h4>
                    <p className="text-agentic-text-secondary leading-relaxed text-sm">
                      {selectedAgentType.description || 'This AI assistant helps automate workflows, route requests, and improve response quality for your teams.'}
                    </p>
                  </div>
                  <div className="rounded-2xl border border-agentic-gray-200 p-5">
                    <h4 className="text-xl font-semibold text-agentic-text-primary mb-3">Key Features</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {(selectedCapabilities.length ? selectedCapabilities : ['Fast setup', 'Actionable outputs', 'Reliable handling', 'Scalable workflows'])
                        .slice(0, 4)
                        .map((feature, idx) => {
                          const bg =
                            idx % 4 === 0
                              ? 'bg-blue-50'
                              : idx % 4 === 1
                                ? 'bg-green-50'
                                : idx % 4 === 2
                                  ? 'bg-purple-50'
                                  : 'bg-orange-50';
                          return (
                            <div key={feature} className={`rounded-2xl p-3.5 flex items-center gap-2.5 ${bg}`}>
                              <Sparkles className="h-4.5 w-4.5 text-agentic-primary-blue" />
                              <span className="text-agentic-text-primary text-sm font-semibold">{toCapabilityLabel(feature)}</span>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                  <div className="rounded-2xl border border-agentic-gray-200 p-5">
                    <h4 className="text-xl font-semibold text-agentic-text-primary mb-3">Tags</h4>
                    <div className="flex flex-wrap gap-3">
                      {(selectedCapabilities.length ? selectedCapabilities : ['agent automation', 'workflow support', 'service operations'])
                        .map((tag) => (
                          <Badge tone="neutral"
                            key={tag}
                            className="inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold"
                          >
                            {toCapabilityLabel(tag)}
                          </Badge>
                        ))}
                    </div>
                  </div>
                </>
              )}

              {detailsTab === 'skills' && (
                <div className="rounded-2xl border border-agentic-gray-200 p-5">
                  <h4 className="text-xl font-semibold text-agentic-text-primary mb-3">Skills & Capabilities</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {(selectedCapabilities.length ? selectedCapabilities : ['task management', 'information retrieval', 'response generation', 'workflow orchestration']).map((skill) => (
                      <div key={skill} className="rounded-xl border border-agentic-gray-200 bg-agentic-gray-50 px-4 py-3 flex items-center gap-2">
                        <Sparkles className="h-4 w-4 text-agentic-primary-blue" />
                        <span className="text-sm font-medium text-agentic-text-primary">{toCapabilityLabel(skill)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {detailsTab === 'performance' && (
                <div className="rounded-2xl border border-agentic-gray-200 p-5">
                  <h4 className="text-xl font-semibold text-agentic-text-primary mb-3">Performance</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="rounded-xl border border-agentic-gray-200 bg-agentic-gray-50 p-4">
                      <div className="text-sm text-agentic-text-secondary">Views</div>
                      <div className="text-xl font-semibold text-agentic-text-primary mt-1">{toStableViews(selectedAgentType.id)}</div>
                    </div>
                    <div className="rounded-xl border border-agentic-gray-200 bg-agentic-gray-50 p-4">
                      <div className="text-sm text-agentic-text-secondary">Success Rate</div>
                      <div className="text-xl font-semibold text-agentic-text-primary mt-1">{toStableSuccessRate(selectedAgentType.id)}%</div>
                    </div>
                    <div className="rounded-xl border border-agentic-gray-200 bg-agentic-gray-50 p-4">
                      <div className="text-sm text-agentic-text-secondary">Avg Latency</div>
                      <div className="text-xl font-semibold text-agentic-text-primary mt-1">{toStableLatency(selectedAgentType.id)}ms</div>
                    </div>
                  </div>
                </div>
              )}

              {detailsTab === 'technical' && (
                <div className="rounded-2xl border border-agentic-gray-200 p-5 space-y-4">
                  <h4 className="text-xl font-semibold text-agentic-text-primary">Technical Details</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="rounded-xl border border-agentic-gray-200 bg-agentic-gray-50 p-4">
                      <div className="text-sm text-agentic-text-secondary">Code</div>
                      <div className="mt-1 text-sm font-semibold text-agentic-text-primary">{selectedAgentType.code || 'N/A'}</div>
                    </div>
                    <div className="rounded-xl border border-agentic-gray-200 bg-agentic-gray-50 p-4">
                      <div className="text-sm text-agentic-text-secondary">Industry</div>
                      <div className="mt-1 text-sm font-semibold text-agentic-text-primary">{selectedAgentType.industry?.name || 'General'}</div>
                    </div>
                    <div className="rounded-xl border border-agentic-gray-200 bg-agentic-gray-50 p-4">
                      <div className="text-sm text-agentic-text-secondary">Policies</div>
                      <div className="mt-1 text-sm font-semibold text-agentic-text-primary">{selectedPolicies.length}</div>
                    </div>
                    <div className="rounded-xl border border-agentic-gray-200 bg-agentic-gray-50 p-4">
                      <div className="text-sm text-agentic-text-secondary">Status</div>
                      <div className="mt-1 text-sm font-semibold text-agentic-text-primary">
                        {selectedAgentType.isActive ? 'Active' : 'Inactive'}
                      </div>
                    </div>
                  </div>
                  {!!selectedPolicies.length && (
                    <div>
                      <div className="text-sm font-medium text-agentic-text-secondary mb-2">Applied Policies</div>
                      <div className="flex flex-wrap gap-2">
                        {selectedPolicies.map((policy) => (
                          <span key={policy} className="inline-flex items-center rounded-full bg-agentic-gray-100 px-3 py-1 text-sm font-semibold text-agentic-text-primary">
                            <Cpu className="h-3.5 w-3.5 mr-1.5 text-agentic-primary-blue" />
                            {toCapabilityLabel(policy)}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {isHireConfirmOpen && selectedAgentType && (
        <Overlay className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/45" onClick={() => setIsHireConfirmOpen(false)} />
          <Surface className="relative w-full max-w-xl overflow-hidden rounded-2xl border border-agentic-gray-200 bg-white shadow-2xl">
            <div className="border-b border-agentic-gray-200 px-6 py-4">
              <h3 className="text-xl font-semibold text-agentic-text-primary">Hire This Agent</h3>
            </div>

            <div className="px-6 py-7">
              <div className="mx-auto mb-5 flex h-20 w-20 items-center justify-center rounded-full bg-agentic-gray-50 text-agentic-primary-blue shadow-sm">
                <UserPlus className="h-11 w-11" />
              </div>

              <h4 className="text-center text-3xl font-semibold leading-tight text-agentic-text-primary">
                {hireModalPlans.length > 0
                  ? `Choose a plan for ${selectedAgentType.name}`
                  : `Do you want to hire ${selectedAgentType.name}?`}
              </h4>
              <p className="mx-auto mt-3 max-w-lg text-center text-base leading-relaxed text-agentic-text-secondary">
                {hireModalPlans.length > 0
                  ? entitlementForSelected && hireModalPlans.length > 1
                    ? 'You already have access. Pick another plan to start checkout for a different tier (manage billing in Stripe when needed).'
                    : 'Complete payment in Stripe, then return here to finish setup from the Agents page.'
                  : 'This agent will be added to your team and you can start training.'}
              </p>

              <div className="mt-6 rounded-xl border border-agentic-primary-blue/35 bg-agentic-gray-50 px-4 py-3">
                <div className="flex items-start gap-3">
                  <Bot className="mt-0.5 h-5 w-5 text-agentic-primary-blue" />
                  <div>
                    <p className="text-lg font-semibold text-agentic-text-primary">{selectedAgentType.name}</p>
                    <p className="text-sm text-agentic-text-secondary">{selectedAgentType.industry?.name || 'General'}</p>
                    <div className="mt-2">
                      <PricingTierTag tier={getAgentPricingTier(selectedAgentType)} />
                    </div>
                  </div>
                </div>
              </div>

              {billingPlansLoading && hireModalPlans.length === 0 && (
                <p className="mt-4 text-center text-sm text-agentic-text-secondary">Loading plans…</p>
              )}

              {hireModalPlans.length > 0 && (
                <div className="mt-5 space-y-2">
                  {hireModalPlans.some((p) => p.id.startsWith(METADATA_PLAN_ID_PREFIX)) && (
                    <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-950">
                      Plans are shown from agent metadata until billing syncs to Stripe. Checkout stays disabled until a synced billing plan exists.
                    </p>
                  )}
                  {hireModalPlans.map((plan) => {
                    const isCurrent = planMatchesEntitlement(plan, entitlementForSelected);
                    return (
                      <label
                        key={plan.id}
                        className={`flex items-center gap-3 rounded-xl border px-4 py-3 ${
                          isCurrent
                            ? 'cursor-not-allowed border-agentic-gray-200 bg-agentic-gray-50/90'
                            : 'cursor-pointer'
                        } ${
                          selectedPlanId === plan.id && !isCurrent
                            ? 'border-agentic-primary-blue bg-blue-50/60'
                            : !isCurrent
                              ? 'border-agentic-gray-200 bg-white'
                              : ''
                        }`}
                      >
                        <input
                          type="radio"
                          name="billing-plan"
                          className="h-4 w-4"
                          disabled={isCurrent}
                          checked={selectedPlanId === plan.id}
                          onChange={() => setSelectedPlanId(plan.id)}
                        />
                        <div className="min-w-0 flex-1">
                          <p className="flex flex-wrap items-center gap-2 font-semibold text-agentic-text-primary">
                            {plan.displayName}
                            {isCurrent && (
                              <Badge className="rounded-full bg-agentic-gray-200 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-agentic-text-secondary">
                                Current
                              </Badge>
                            )}
                          </p>
                          {plan.description && (
                            <p className="text-xs text-agentic-text-secondary line-clamp-2">{plan.description}</p>
                          )}
                          <p className="mt-1 text-sm text-agentic-text-primary">
                            <span className="font-medium">{formatPlanPrice(plan.amountMinor, plan.currency)}</span>
                            <span className="text-agentic-text-secondary"> · {formatPlanBillingCadence(plan)}</span>
                          </p>
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}

              <div className="mt-8 flex items-center justify-end gap-3">
                <Button tone="custom"
                  type="button"
                  onClick={() => setIsHireConfirmOpen(false)}
                  disabled={hiring}
                  className="rounded-xl border border-agentic-gray-300 px-4 py-2 text-base font-medium text-agentic-text-primary hover:bg-agentic-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  Cancel
                </Button>
                <Button tone="custom"
                  type="button"
                  onClick={confirmHireAgent}
                  disabled={hireModalPrimaryDisabled}
                  className="rounded-xl bg-black px-4 py-2 text-base font-medium text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {hiring
                    ? hireModalPlans.length > 0
                      ? 'Redirecting…'
                      : 'Hiring...'
                    : hireModalPrimaryLabel}
                </Button>
              </div>
            </div>
          </Surface>
        </Overlay>
      )}

      {/* Create / Edit Modal */}
      {isModalOpen && (
        <Overlay className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/30" onClick={() => { setIsModalOpen(false); resetForm(); }}></div>
          <Surface tone="popover" className="relative w-full max-w-4xl mx-4 rounded-2xl border p-6 max-h-[85vh] overflow-y-auto">
            <div className="mb-4">
              <h2 className="text-xl font-semibold text-agentic-text-primary">
                {isEditing ? 'Edit Agent Type' : 'Create Agent Type'}
              </h2>
              <p className="text-sm text-agentic-text-secondary mt-1">
                {isEditing ? 'Update the agent type details.' : 'Define a new agent type identifier.'}
              </p>
            </div>
            {error && (
              <div className="mb-3 px-3 py-2 rounded-lg text-sm bg-red-50 text-red-700 border border-red-200">{error}</div>
            )}
            <form onSubmit={handleCreateOrUpdate} className="space-y-6">
              {/* Name */}
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="block text-sm font-medium text-agentic-text-primary mb-1">
                    Name<span className="text-red-500"> *</span>
                  </label>
                  <TextField tone="default-focus-primary"
                    value={newTypeName}
                    onChange={(e) => setNewTypeName(e.target.value)}
                    placeholder="Research Agent"
                    className="w-full px-3 py-2 rounded-lg border focus:ring-2"
                  />
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-agentic-text-primary mb-1">Description</label>
                <TextArea tone="default-focus-primary"
                  value={newTypeDescription}
                  onChange={(e) => setNewTypeDescription(e.target.value)}
                  placeholder="Short description of what agents of this type generally do"
                  className="w-full px-3 py-2 rounded-lg border focus:ring-2"
                  rows={4}
                />
              </div>

              {/* Status */}
              <div className="flex items-center justify-between py-2 border rounded-lg px-3 border-gray-200">
                <div>
                  <div className="text-sm font-medium text-agentic-text-primary">Status</div>
                  <div className="text-xs text-agentic-text-secondary">Disable to keep the type unavailable until ready.</div>
                </div>
                <label className="inline-flex items-center space-x-2">
                  <Checkbox checked={newTypeActive} onChange={(e) => setNewTypeActive(e.target.checked)} />
                  <span className="text-sm text-agentic-text-primary">Active</span>
                </label>
              </div>

              {/* Capabilities */}
              <div>
                <div className="text-sm font-medium text-agentic-text-primary mb-2">Capabilities</div>
                {newTypeCapabilities.length === 0 && (
                  <p className="text-sm text-agentic-text-secondary mb-2">No capabilities yet. Click Add to define one.</p>
                )}
                <div className="space-y-2 mb-2">
                  {newTypeCapabilities.map((cap, idx) => (
                    <div key={`cap-${idx}`} className="flex items-center gap-2">
                      <TextField tone="default-focus-primary"
                        value={cap}
                        onChange={(e) => {
                          const v = e.target.value;
                          setNewTypeCapabilities((prev) => prev.map((c, i) => (i === idx ? v : c)));
                        }}
                        placeholder="e.g., web-search"
                        className="flex-1 px-3 py-2 rounded-lg border focus:ring-2"
                      />
                      <Button tone="custom"
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          setNewTypeCapabilities((prev) => prev.filter((_, i) => i !== idx));
                        }}
                        className="px-3 py-2 rounded-lg border border-gray-200 text-red-600 hover:bg-red-50"
                      >
                        Remove
                      </Button>
                    </div>
                  ))}
                </div>
                <Button tone="custom"
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    setNewTypeCapabilities((prev) => [...prev, '']);
                  }}
                  className="px-3 py-2 rounded-lg border border-gray-200 text-agentic-text-primary hover:bg-gray-50"
                >
                  + Add Capability
                </Button>
              </div>

           


              {/* Policies */}
              <div>
                <div className="text-sm font-medium text-agentic-text-primary mb-2">Policies</div>
                {newTypePolicies.length === 0 && (
                  <p className="text-sm text-agentic-text-secondary mb-2">No policies yet. Click Add to define one.</p>
                )}
                <div className="space-y-2 mb-2">
                  {newTypePolicies.map((pol, idx) => (
                    <div key={`pol-${idx}`} className="flex items-center gap-2">
                      <TextField tone="default-focus-primary"
                        value={pol}
                        onChange={(e) => {
                          const v = e.target.value;
                          setNewTypePolicies((prev) => prev.map((p, i) => (i === idx ? v : p)));
                        }}
                        placeholder="e.g., no-sensitive-data"
                        className="flex-1 px-3 py-2 rounded-lg border focus:ring-2"
                      />
                      <Button tone="custom"
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          setNewTypePolicies((prev) => prev.filter((_, i) => i !== idx));
                        }}
                        className="px-3 py-2 rounded-lg border border-gray-200 text-red-600 hover:bg-red-50"
                      >
                        Remove
                      </Button>
                    </div>
                  ))}
                </div>
                <Button tone="custom"
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    setNewTypePolicies((prev) => [...prev, '']);
                  }}
                  className="px-3 py-2 rounded-lg border border-gray-200 text-agentic-text-primary hover:bg-gray-50"
                >
                  + Add Policy
                </Button>
              </div>

              {/* Metadata JSON */}
              <div>
                <label className="block text-sm font-medium text-agentic-text-primary mb-1">Metadata (JSON)</label>
                <TextArea tone="default-focus-primary"
                  value={newTypeMetadata}
                  onChange={(e) => setNewTypeMetadata(e.target.value)}
                  placeholder='{"maxConcurrency": 4}'
                  className="w-full px-3 py-2 rounded-lg border focus:ring-2 font-mono text-sm"
                  rows={5}
                />
              </div>

              <div className="flex items-center justify-end space-x-3 pt-2">
                <Button tone="outline-muted"
                  type="button"
                  onClick={() => { setIsModalOpen(false); resetForm(); }}
                  className="px-4 py-2 rounded-lg border"
                >
                  Cancel
                </Button>
                <Button tone="primary-gradient"
                  type="submit"
                  className="px-4 py-2 rounded-lg"
                >
                  {isEditing ? 'Save Changes' : 'Create'}
                </Button>
              </div>
            </form>
          </Surface>
        </Overlay>
      )}

      <ConfirmationDialog
        isOpen={confirmOpen}
        onClose={() => { setConfirmOpen(false); setDeleteId(null); }}
        onConfirm={doDelete}
        title="Confirm Delete"
        message="Are you sure you want to delete this agent type?"
        confirmText="Delete"
        cancelText="Cancel"
        loading={deleting}
        variant="error"
      />

    </Page>
  );
};
